import { BusinessObjectBase } from './base';
import { processDataTypeSchema } from '@schemas/business-objects/process-data-type';
import type { ProcessDataProperty } from './process-data-property';
import type { ProcessDataValue } from './process-data-value';
import type { ProcessFunction } from './process-function';
import type { ProcessFunctionParameter } from './process-function-parameter';
import type { ProcessMessageProperty } from './process-message-property';
import type { ProcessServiceProperty } from './process-service-property';
import type { ProcessUserInterfaceProperty } from './process-user-interface-property';
import type { Id, ProcessDataNativeType } from '../base-types';

export interface ProcessDataTypeData {
  readonly id: Id;
  readonly name?: string;
  readonly displayName?: string;
  readonly description?: string;
  readonly nativeType?: ProcessDataNativeType;
  readonly values?: ProcessDataValue[];
  readonly properties?: ProcessDataProperty[];
  readonly usedByDataProperties?: ProcessDataProperty[];
  readonly usedByUIProperties?: ProcessUserInterfaceProperty[];
  readonly usedByServiceProperties?: ProcessServiceProperty[];
  readonly usedByMessageProperties?: ProcessMessageProperty[];
  readonly usedByFunctionParameters?: ProcessFunctionParameter[];
  readonly usedByFunctions?: ProcessFunction[];
}

export interface ProcessDataType extends Readonly<ProcessDataTypeData> {}
type ProcessDataTypeCreateWithChildrenData = Partial<
  Omit<ProcessDataTypeData, 'values' | 'properties'>
> & {
  values?: Partial<ProcessDataValue>[];
  properties?: Partial<ProcessDataProperty>[];
};

export class ProcessDataType extends BusinessObjectBase {
  static readonly entityName = 'ProcessDataType' as const;
  static readonly plural = 'process-data-types';
  static schema = processDataTypeSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<ProcessDataType[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new ProcessDataType(data));
  }

  static async findOne(filter?: any): Promise<ProcessDataType | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new ProcessDataType(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<ProcessDataType> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new ProcessDataType(data);
  }

  static async create(
    data: Omit<ProcessDataTypeData, 'id'>,
  ): Promise<ProcessDataType> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new ProcessDataType(result);
  }

  static async createMany(
    data: Omit<ProcessDataTypeData, 'id'>[],
  ): Promise<ProcessDataType[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessDataType(item));
  }

  static async createWithChildren(
    data: ProcessDataTypeCreateWithChildrenData,
  ): Promise<ProcessDataType> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new ProcessDataType(result);
  }

  static async createManyWithChildren(
    data: ProcessDataTypeCreateWithChildrenData[],
  ): Promise<ProcessDataType[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessDataType(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<ProcessDataTypeData>,
  ): Promise<ProcessDataType> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new ProcessDataType(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
